<template>
  <div
    :class="['splash-screen', exitClass]"
    aria-live="polite"
    aria-busy="true"
  >
    <div class="splash-container">
      <div class="logo-container">
        <img src="/img/logo.svg" alt="CrowdHRs Logo" class="logo" />
        <div v-if="loggedInUser" class="welcome-message">
          Welcome back, {{ loggedInUser.fullname }}!
        </div>
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
          <Button
            class="chrs-btn-primary"
            @click="retryFetchData"
            aria-label="Try Again"
          >
            <i class="pi pi-refresh mr-1"></i>
            Try Again
          </Button>
        </div>
        <div v-if="!errorMessage" class="progress-container">
          <ProgressBar mode="indeterminate" class="custom-progress-bar" />
        </div>
        <div class="copyright">
          &copy; {{ currentYear }} CrowdHRs. All rights reserved.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get, post } from '@/api/Services'
import { account } from '@/store/account.module'
import { getItem } from '@/store/localStorage'
import ProgressBar from 'primevue/progressbar'

export default {
  name: 'InitialSplashScreen',
  components: {
    ProgressBar,
  },
  data() {
    return {
      apiLoaded: false,
      exitClass: '',
      apiRequests: [
        { name: 'sqlQuery', completed: false, success: false },
        { name: 'genders', completed: false, success: false },
      ],
      loggedInUser: null,
      currentYear: new Date().getFullYear(),
      errorMessage: null,
      maxRetries: 3,
      currentRetries: 0,
    }
  },
  methods: {
    async fetchInitialData() {
      try {
        // Kullanıcı bilgilerini store'dan al
        if (account.state.loggeduser) {
          this.loggedInUser = account.state.loggeduser
        }

        // SQL ID 42'ye istek at
        try {
          const params = {
            sqlId: 42,
            parameters: JSON.stringify([]),
          }
          const sqlResponse = await post('Commons/Executer', params)
          if (sqlResponse) {
            this.apiRequests[0].completed = true
            this.apiRequests[0].success = true
          } else {
            throw new Error('SQL query failed')
          }
        } catch (error) {
          console.error('SQL query error:', error)
          this.apiRequests[0].completed = true
          this.apiRequests[0].success = false

          // Handle specific HTTP error codes
          if (error.response) {
            const statusCode = error.response.status
            if (statusCode === 404) {
              this.errorMessage =
                'Server resource not found (404). Please contact support.'
            } else if (statusCode === 500) {
              this.errorMessage =
                'Server error (500). Please try again later or contact support.'
            } else {
              this.errorMessage = `Server error (${statusCode}). Please check your connection and try again.`
            }
          } else if (error.request) {
            // Request was made but no response received
            this.errorMessage =
              'No response from server. Please check your connection and try again.'
          } else {
            this.errorMessage =
              'Failed to load required data. Please check your connection and try again.'
          }
        }

        // Genders endpoint'ine istek at
        if (!this.errorMessage) {
          try {
            const gendersResponse = await get('Genders')
            if (gendersResponse) {
              this.apiRequests[1].completed = true
              this.apiRequests[1].success = true
            } else {
              throw new Error('Genders request failed')
            }
          } catch (error) {
            console.error('Genders request error:', error)
            this.apiRequests[1].completed = true
            this.apiRequests[1].success = false

            // Handle specific HTTP error codes
            if (error.response) {
              const statusCode = error.response.status
              if (statusCode === 404) {
                this.errorMessage =
                  'Server resource not found (404). Please contact support.'
              } else if (statusCode === 500) {
                this.errorMessage =
                  'Server error (500). Please try again later or contact support.'
              } else {
                this.errorMessage = `Server error (${statusCode}). Please check your connection and try again.`
              }
            } else if (error.request) {
              // Request was made but no response received
              this.errorMessage =
                'No response from server. Please check your connection and try again.'
            } else {
              this.errorMessage =
                'Failed to load required data. Please check your connection and try again.'
            }
          }
        }

        this.checkAllRequestsCompleted()
      } catch (error) {
        console.error('API isteği sırasında hata oluştu:', error)

        // Handle global errors
        if (error.response) {
          const statusCode = error.response.status
          if (statusCode === 404) {
            this.errorMessage =
              'Server resource not found (404). Please contact support.'
          } else if (statusCode === 500) {
            this.errorMessage =
              'Server error (500). Please try again later or contact support.'
          } else {
            this.errorMessage = `Server error (${statusCode}). Please check your connection and try again.`
          }
        } else if (error.request) {
          this.errorMessage =
            'No response from server. Please check your connection and try again.'
        } else {
          this.errorMessage =
            'An unexpected error occurred. Please check your connection and try again.'
        }
      }
    },

    checkAllRequestsCompleted() {
      const allCompleted = this.apiRequests.every((req) => req.completed)

      if (allCompleted) {
        const allSuccessful = this.apiRequests.every((req) => req.success)

        if (allSuccessful) {
          // Tüm istekler başarılı ise splash screen'i kapat
          this.apiLoaded = true
          this.closeSplashScreen()
        }
        // Başarısız istek varsa artık otomatik kapatmıyoruz, kullanıcı "Try Again" butonuna tıklamalı
      }
    },

    retryFetchData() {
      // Hata mesajını temizle
      this.errorMessage = null

      // API isteklerini sıfırla
      this.apiRequests.forEach((req) => {
        req.completed = false
        req.success = false
      })

      // Yeniden deneme sayısını artır
      this.currentRetries += 1

      // Eğer maksimum deneme sayısına ulaşıldıysa, kullanıcıya bildir
      if (this.currentRetries >= this.maxRetries) {
        this.errorMessage =
          'Maximum retry attempts reached. Please contact support or try again later.'
        return
      }

      // API isteklerini yeniden başlat
      this.fetchInitialData()
    },

    closeSplashScreen() {
      // Animasyon için çıkış sınıfını ekle
      this.exitClass = 'splash-screen-exit'

      // Animasyon tamamlandıktan sonra görünürlüğü kapat
      setTimeout(() => {
        this.$emit('splash-completed')
      }, 500)
    },
  },
  mounted() {
    // API isteklerini başlat
    this.fetchInitialData()

    // Yedek olarak, API istekleri başarısız olursa 10 saniye sonra hata mesajı göster
    setTimeout(() => {
      if (!this.apiLoaded && !this.errorMessage) {
        this.errorMessage =
          'Request timed out. Please check your connection and try again.'
      }
    }, 10000)
  },
}
</script>

<style lang="scss" scoped>
.splash-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(3px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999; /* Çok yüksek bir z-index değeri */
  animation: fadeIn 0.3s ease-in-out;

  .splash-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    max-width: 90%;
    position: relative;
    padding: 2.5rem;

    .logo-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;

      .logo {
        width: 300px;
        height: auto;
        margin-bottom: 1.5rem;
      }

      .welcome-message {
        font-size: 1.5rem;
        font-weight: 500;
        color: #103660;
        margin-bottom: 1rem;
        text-align: center;
        animation: fadeIn 0.5s ease-in-out;
      }

      .error-message {
        font-size: 1rem;
        font-weight: 500;
        color: #e74c3c;
        margin-bottom: 1rem;
        text-align: center;
        animation: fadeIn 0.5s ease-in-out;
        padding: 1rem 1.5rem;
        background-color: rgba(231, 76, 60, 0.1);
        border-radius: 8px;
        max-width: 80%;
        border: 1px solid rgba(231, 76, 60, 0.3);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
      }

      .retry-button {
        background-color: #fc6c2a;
        color: white;
        border: none;
        border-radius: 4px;
        padding: 0.5rem 1.5rem;
        font-size: 1rem;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

        &:hover {
          background-color: darken(#fc6c2a, 10%);
        }

        &:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(252, 108, 42, 0.4);
        }
      }

      .progress-container {
        width: 300px;
        margin-top: 0.5rem;
        margin-bottom: 2rem;
      }

      .copyright {
        font-size: 0.85rem;
        color: #6c757d;
        text-align: center;
        margin-top: auto;
      }
    }
  }
}

:deep(.custom-progress-bar) {
  height: 6px;
  background-color: rgba(16, 54, 96, 0.1);
  border-radius: 3px;
}

:deep(.custom-progress-bar .p-progressbar-indeterminate) {
  background-color: #fc6c2a;
}

:deep(.custom-progress-bar .p-progressbar-value) {
  background-color: #fc6c2a;
  border-radius: 3px;
}

:deep(.p-progressbar-indeterminate::before) {
  background-color: #fc6c2a;
}

:deep(.p-progressbar-indeterminate::after) {
  background-color: #fc6c2a;
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.splash-screen-exit {
  animation: fadeOut 0.5s ease-in-out forwards;
}

@media (prefers-reduced-motion: reduce) {
  .progress-fill {
    animation-duration: 3.5s !important;
  }
}
</style>
